// src/pages/ApprovePage.tsx
//
// Public route hit from the approval email link:
//   https://app.viashift.app/approve?token=<jwt>
//
// Lives under PublicLayout — the manager may not be signed into the PWA
// when they click the link. This page reads the token from the URL,
// POSTs it to the handle-approval-link Edge Function on mount, and
// renders the result inline. No redirect, no second route.
//
// The Edge Function does the JWT verify + RPC + (stub) vendor email
// trigger; this component is purely UI.

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Icon from '@/components/Icon';

// ---- Result shape (mirrors the Edge Function response) -------------

type ResultStatus =
  | 'approved'
  | 'already_approved'
  | 'invalid'
  | 'expired'
  | 'error';

interface ApiResponse {
  ok: boolean;
  status: ResultStatus;
  order_id?: string;
  reason?: string;
}

// ---- View state ----------------------------------------------------

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'result'; data: ApiResponse }
  | { kind: 'network_error'; message: string };

// ---- Display helpers -----------------------------------------------

interface CardContent {
  title: string;
  body: string;
  tone: 'success' | 'neutral' | 'error';
}

function shortPo(orderId: string | undefined): string {
  if (!orderId) return '';
  return ` (order ${orderId.slice(0, 8)})`;
}

function buildCard(view: ViewState): CardContent {
  if (view.kind === 'network_error') {
    return {
      title: 'Connection problem',
      body: 'We couldn\u2019t reach Stocora to record the approval. Check your connection and try again, or open the app and approve from the Reorder page.',
      tone: 'error',
    };
  }
  if (view.kind !== 'result') {
    // 'loading' / 'idle' don't render via this helper.
    return { title: '', body: '', tone: 'neutral' };
  }
  const { status, order_id } = view.data;
  switch (status) {
    case 'approved':
      return {
        title: 'Order approved',
        body: `The vendor order has been recorded and the email is on its way${shortPo(order_id)}.`,
        tone: 'success',
      };
    case 'already_approved':
      return {
        title: 'Already approved',
        body: `This order was approved earlier${shortPo(order_id)}. No further action needed.`,
        tone: 'neutral',
      };
    case 'expired':
      return {
        title: 'Approval link expired',
        body: 'Approval links are valid for 7 days. Open Stocora and approve the order from the Reorder page.',
        tone: 'error',
      };
    case 'invalid':
      return {
        title: 'Approval link invalid',
        body: 'This link can\u2019t be used. The queue may have changed since the email was sent. Open Stocora to review.',
        tone: 'error',
      };
    case 'error':
    default:
      return {
        title: 'Something went wrong',
        body: 'Approval didn\u2019t complete. Open Stocora and try again, or contact support if this keeps happening.',
        tone: 'error',
      };
  }
}

const TONE_CARD: Record<CardContent['tone'], string> = {
  success: 'border-emerald-200 bg-emerald-50',
  neutral: 'border-slate-200 bg-white',
  error: 'border-rose-200 bg-rose-50',
};

const TONE_TEXT: Record<CardContent['tone'], string> = {
  success: 'text-emerald-900',
  neutral: 'text-slate-900',
  error: 'text-rose-900',
};

const TONE_ICON_NAME: Record<CardContent['tone'], 'check' | 'clock' | 'shield'> = {
  success: 'check',
  neutral: 'clock',
  error: 'shield',
};

const TONE_ICON_BG: Record<CardContent['tone'], string> = {
  success: 'bg-emerald-600 text-white',
  neutral: 'bg-slate-200 text-slate-700',
  error: 'bg-rose-600 text-white',
};

// ---- Component -----------------------------------------------------

export default function ApprovePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [view, setView] = useState<ViewState>({ kind: 'idle' });

  // StrictMode in dev double-mounts effects. Guard so the function only
  // gets called once per token.
  const submittedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setView({
        kind: 'result',
        data: { ok: false, status: 'invalid', reason: 'missing_token' },
      });
      return;
    }
    if (submittedRef.current) return;
    submittedRef.current = true;

    setView({ kind: 'loading' });

    let cancelled = false;
    void (async () => {
      try {
        const { data, error } = await supabase.functions.invoke<ApiResponse>(
          'handle-approval-link',
          { body: { token } },
        );
        if (cancelled) return;
        if (error) {
          // FunctionsHttpError surfaces non-2xx as `error` while still
          // returning the body in `data`. Prefer the body's structured
          // status if present.
          if (data && typeof data === 'object' && 'status' in data) {
            setView({ kind: 'result', data });
          } else {
            setView({
              kind: 'network_error',
              message: error.message || 'invoke_failed',
            });
          }
          return;
        }
        if (!data) {
          setView({
            kind: 'result',
            data: { ok: false, status: 'error', reason: 'empty_response' },
          });
          return;
        }
        setView({ kind: 'result', data });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setView({ kind: 'network_error', message: msg });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // ---- Render ------------------------------------------------------

  if (view.kind === 'idle' || view.kind === 'loading') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <span
          className="w-8 h-8 border-[3px] border-slate-200 border-t-action rounded-full animate-spin"
          role="status"
          aria-label="Processing approval"
        />
        <p className="text-sm text-slate-600 mt-4">Recording approval\u2026</p>
      </div>
    );
  }

  const card = buildCard(view);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
      <div
        className={`w-full max-w-[340px] rounded-xl border p-5 shadow-sm ${TONE_CARD[card.tone]} ${TONE_TEXT[card.tone]}`}
      >
        <div className="flex items-start gap-3 mb-2">
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${TONE_ICON_BG[card.tone]}`}
            aria-hidden="true"
          >
            <Icon name={TONE_ICON_NAME[card.tone]} className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <h1 className="text-base font-semibold leading-tight pt-1">
            {card.title}
          </h1>
        </div>
        <p className="text-sm leading-relaxed mb-5 pl-11">{card.body}</p>
        <div className="pl-11">
          <Link
            to="/reorder"
            className="inline-flex items-center justify-center text-sm font-medium px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition min-h-[44px]"
          >
            Open Stocora
          </Link>
        </div>
      </div>
    </div>
  );
}
